import React, { useEffect, useState, useCallback } from 'react';
import { App, ConfigProvider, message, theme } from 'antd';
import { antdTheme, darkAntdTheme } from './audioTheme';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextValue {
  themeMode: ThemeMode;
  isDark: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

export const ThemeContext = React.createContext<ThemeContextValue>({
  themeMode: 'auto',
  isDark: false,
  setThemeMode: () => {},
  toggleTheme: () => {},
});

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const AudioThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    // 从 localStorage 读取保存的主题偏好
    const saved = localStorage.getItem('themeMode') as ThemeMode;
    return saved && ['light', 'dark', 'auto'].includes(saved) ? saved : 'auto';
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const isDark = themeMode === 'dark' || (themeMode === 'auto' && systemPrefersDark);

  // 监听系统主题变化
  useEffect(() => {
    if (themeMode !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    // 现代浏览器
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // 旧浏览器兼容
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [themeMode]);

  // 设置主题模式
  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('themeMode', mode);
    
    // 更新 HTML 元素的 data-theme 属性
    document.documentElement.setAttribute('data-theme', mode === 'auto' 
      ? (systemPrefersDark ? 'dark' : 'light')
      : mode
    );
  }, [systemPrefersDark]);

  // 切换主题
  const toggleTheme = useCallback(() => {
    if (themeMode === 'auto') {
      setThemeMode('dark');
    } else if (themeMode === 'dark') {
      setThemeMode('light');
    } else {
      setThemeMode('auto');
    }
  }, [themeMode, setThemeMode]);

  // 当主题变化时更新 HTML 属性
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    // 添加过渡类以支持平滑切换
    document.documentElement.classList.add('theme-transition');
    const timer = setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
    }, 300);
    
    return () => clearTimeout(timer);
  }, [isDark]);

  // 配置 message 的全局设置
  useEffect(() => {
    message.config({
      top: 100,
      duration: 3,
      maxCount: 3,
      rtl: false,
    });
  }, []);

  const contextValue: ThemeContextValue = {
    themeMode,
    isDark,
    setThemeMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider 
        theme={isDark ? {
          ...darkAntdTheme,
          algorithm: theme.darkAlgorithm,
        } : {
          ...antdTheme,
          algorithm: theme.defaultAlgorithm,
        }}
      >
        <App>{children}</App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

// Hook 用于在组件中使用主题
export const useTheme = (): ThemeContextValue => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within AudioThemeProvider');
  }
  return context;
}; 